import { Copy, GraduationCap } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import type { Child } from '../lib/types';
import { useParent } from './ParentContext';
import { childHas, PlanLocked } from './Plans';

// A child's settings on their Features tab (#833): hiding School Resources (#829), camera blur (#592), turning in work
// (#338) and the Student Portal. They used to be on the Profile tab.

/**
 * Camera blur (#592), as one more switch in the Features card's Collaboration group, beside Live view (#833): whether
 * the child may blur their camera background from the ClassGuardian menu on their computer. Off with live view.
 */
export function CameraBlurSwitch({ child, liveViewOn }: { child: Child; liveViewOn: boolean }) {
  const { parent, replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  if (!parent.cameraBlur?.forChildren) return null;
  async function set(on: boolean) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { blurAllowed: on }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const allowed = child.blurAllowed ?? true;
  return (
    <label className={`check ${liveViewOn ? '' : 'muted'}`}>
      <input type="checkbox" checked={liveViewOn && allowed} disabled={busy || !liveViewOn} onChange={(e) => set(e.target.checked)} />
      <span>
        Camera blur
        <span className="muted small block">{child.name} can blur their background from the ClassGuardian menu</span>
      </span>
    </label>
  );
}

/**
 * Hidden mode (#829): hide School Resources on the child's computers. No tray or menu-bar icon and no button; blocking,
 * alerts, live view and attendance keep running, and the panel comes up only for a message or the end of school.
 */
export function SchoolResourcesSection({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  const hidden = !!child.agentHidden;
  // An admin can turn the choice off for everyone; a child already hidden still shows it, so it can be turned back.
  if (child.agentHideAllowed === false && !hidden) return null;
  async function set(on: boolean) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { agentHidden: on }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card stack">
      <h2>School Resources on {child.name}’s computer</h2>
      <label className="check">
        <input type="checkbox" checked={hidden} disabled={busy} onChange={(e) => set(e.target.checked)} />
        <span>
          <strong>Hide School Resources</strong>
          <span className="muted small block">
            No icon in the taskbar or menu bar, and the panel stays closed. Everything you set still works in the background.
          </span>
        </span>
      </label>
      {hidden && (
        <>
          <div className="hidden-mode-grid">
            <div className="hidden-mode-card keeps">
              <strong>Keeps working</strong>
              <span>Blocking apps and sites · alerts · live view · attendance · app time</span>
            </div>
            <div className="hidden-mode-card sees">
              <strong>{child.name} sees only</strong>
              <span>A small notice when an app is closed, and the panel when you send a message or end school early</span>
            </div>
          </div>
          <p className="muted small">
            Planner, grades and chat stay in {child.name}’s Student Portal. On a Mac, macOS still shows its own screen-recording dot during live view.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * Student Portal access (#136): the parent turns it on and sets the code + password the child uses to
 * sign in at /student. The password is shown once; afterwards the parent can reset it or turn it off.
 */
export function StudentPortalSection({ child }: { child: Child }) {
  if (!childHas(child, 'student_portal')) return <PlanLocked label="The Student Portal" />;
  return <StudentPortalAccess child={child} />;
}

function StudentPortalAccess({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const portal = child.studentPortal ?? { enabled: false, code: null };
  const [creds, setCreds] = useState<{ code: string; password: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function enableOrReset() {
    setBusy(true);
    try {
      const r = await api.post<{ code: string; password: string }>(`/api/children/${child.id}/student-access`);
      setCreds({ code: r.code, password: r.password });
      replaceChild(await api.get<Child>(`/api/children/${child.id}`));
      toast({ text: portal.enabled ? 'New student password set.' : 'Student portal turned on.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!confirm(`Turn off ${child.name}’s student portal? They won’t be able to sign in until you turn it back on.`)) return;
    try {
      await api.del(`/api/children/${child.id}/student-access`);
      setCreds(null);
      replaceChild(await api.get<Child>(`/api/children/${child.id}`));
      toast({ text: 'Student portal turned off.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <section className="card stack">
      <h2 className="with-icon">
        <GraduationCap size={18} /> Student portal
      </h2>
      <p className="muted small">
        Let {child.name} sign in at <code>/student</code> to see their planner, grades, chat, and whiteboard. You generate the password and can turn access
        off anytime. They only ever see their own information.
      </p>
      {portal.locked && (
        <p className="notice with-icon" role="alert">
          {child.name}’s Student Portal account is locked after too many failed sign-ins. Reset the password below to unlock it.
        </p>
      )}
      {portal.enabled && (
        <div className="row-between">
          <div>
            <div className="muted small">Sign-in code</div>
            <div className="student-code-display">{portal.code}</div>
          </div>
          <span className="badge good">On</span>
        </div>
      )}
      {creds && (
        <div className="student-creds">
          <p className="small">
            <strong>Give these to {child.name} now</strong> — the password is shown only once.
          </p>
          <CredRow label="Code" value={creds.code} />
          <CredRow label="Password" value={creds.password} />
        </div>
      )}
      <div className="row-between">
        {portal.enabled ? (
          <>
            <button className="btn danger-text" onClick={disable}>
              Turn off
            </button>
            <button className="btn" onClick={enableOrReset} disabled={busy}>
              {busy ? 'Working…' : 'Reset password'}
            </button>
          </>
        ) : (
          <button className="btn primary" onClick={enableOrReset} disabled={busy}>
            {busy ? 'Working…' : 'Turn on student portal'}
          </button>
        )}
      </div>
    </section>
  );
}

function CredRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="student-cred-row">
      <span className="muted small">{label}</span>
      <code>{value}</code>
      <button className="btn small ghost" onClick={() => navigator.clipboard?.writeText(value)} aria-label={`Copy ${label}`}>
        <Copy size={14} />
      </button>
    </div>
  );
}

/** Turning in work (#338): require "What I learned today" when the student turns an assignment in. */
export function TurnInSection({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  async function set(on: boolean) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { requireLearned: on }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card stack">
      <h2>Turning in work</h2>
      <label className="check">
        <input type="checkbox" checked={!!child.requireLearned} disabled={busy} onChange={(e) => set(e.target.checked)} />
        <span>
          <strong>Require “What I learned today”</strong>
          <span className="muted small block">
            {child.name} has to write what they learned before an assignment can be turned in, in the Student Portal and in School Resources on their
            computer.
          </span>
        </span>
      </label>
    </section>
  );
}
